import React, { useMemo, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import WbSunnyOutlinedIcon from '@mui/icons-material/WbSunnyOutlined';
import GrainOutlinedIcon from '@mui/icons-material/GrainOutlined';
import GraphicEqOutlinedIcon from '@mui/icons-material/GraphicEqOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import PianoOutlinedIcon from '@mui/icons-material/PianoOutlined';
import TravelExploreOutlinedIcon from '@mui/icons-material/TravelExploreOutlined';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import LockIcon from '@mui/icons-material/Lock';
import {
  SCALE_TYPES,
  MODE_NAMES_BY_SCALE,
  getScaleTypeForMode,
  getTierForMode
} from '../utils/musicTheory';
import { useUserSetting } from '../data/hooks/useUserSetting';
import { useEntitlement } from '../hooks/useEntitlement';
import { useAccessSyncing } from '../data/DataProvider';
import { SELECTED_BG, SELECTED_FG, SELECTED_BG_HOVER } from '../config/uiTheme';

const SCALE_ENTRY_KEY = 'upscale.scaleEntry';

// How many flame icons each tier shows: essentials = none (safe default),
// colors = 1, wild = 2. The badge reads as "how spicy the sound gets".
const TIER_FLAMES = {
  essentials: 0,
  colors: 1,
  wild: 2
};

// Short "sounds like" taglines for the curated, popular modes. Modes without an
// entry simply render without a subtitle (name + badge only).
const MODE_TAGLINES = {
  Ionian: 'Bright, happy major',
  Dorian: 'Minor with a lift',
  Phrygian: 'Dark, Spanish edge',
  Lydian: 'Dreamy, floating',
  Mixolydian: 'Dominant, bluesy',
  Aeolian: 'Natural minor, sad',
  Locrian: 'Unstable, tense',
  'Melodic Minor': 'Smooth jazz minor',
  'Lydian Dominant': 'Bright + bluesy',
  Altered: 'Max tension, jazz',
  'Harmonic Minor': 'Dramatic, classical',
  'Phrygian Dominant': 'Spanish / metal staple',
  'Harmonic Major': 'Bittersweet major',
  Blues: 'Gritty, vocal',
  'Major Pentatonic': 'Open, folk',
  'Minor Pentatonic': 'Rock / blues',
  'Double Harmonic': 'Arabic, intense',
  'Hungarian Minor': 'Gypsy drama',
  'Whole Tone': 'Weightless',
  Hirajoshi: 'Japanese'
};

// One-line caption per scale-type family for the "By name" headers.
const FAMILY_CAPTIONS = {
  Major: '7 modes, bright to dark',
  'Melodic Minor': 'jazz tension',
  'Harmonic Minor': 'dramatic',
  'Harmonic Major': 'cinematic',
  'Double Harmonic': 'exotic, intense',
  'Middle Eastern': 'Spanish / Arabic',
  Japanese: 'sparse, evocative',
  Exotic: 'use your ears',
  Blues: 'six gritty notes',
  'Major Pentatonic': 'five safe notes',
  'Minor Pentatonic': 'rock / blues',
  Symmetrical: 'whole tone / diminished'
};

// Curated mood presets: each maps to 3 hand-picked modes. Independent from the
// app's currentMood system (this list is tighter than getMoodsForScale).
const MOOD_PRESETS = [
  { key: 'bright', icon: WbSunnyOutlinedIcon, label: 'Bright & happy', modes: ['Ionian', 'Lydian', 'Major Pentatonic'] },
  { key: 'sad', icon: GrainOutlinedIcon, label: 'Sad & moody', modes: ['Aeolian', 'Dorian', 'Harmonic Minor'] },
  { key: 'blues', icon: GraphicEqOutlinedIcon, label: 'Bluesy & gritty', modes: ['Minor Pentatonic', 'Blues', 'Mixolydian'] },
  { key: 'dark', icon: BoltOutlinedIcon, label: 'Dark & heavy', modes: ['Phrygian Dominant', 'Phrygian', 'Hungarian Minor'] },
  { key: 'jazz', icon: PianoOutlinedIcon, label: 'Jazz colors', modes: ['Lydian Dominant', 'Altered', 'Melodic Minor'] },
  { key: 'exotic', icon: TravelExploreOutlinedIcon, label: 'Exotic & cinematic', modes: ['Double Harmonic', 'Whole Tone', 'Hirajoshi'] }
];

// Spice rating: 0 flames (essentials) renders nothing, 1 orange flame for colors,
// 2 flames for wild (orange then red).
const FLAME_COLORS = ['oklch(72% 0.16 55)', 'oklch(64% 0.17 25)'];

const FlameRating = ({ tier, dimmed }) => {
  const count = TIER_FLAMES[tier] || 0;
  if (count === 0) return null;
  return (
    <Box component="span" sx={{ display: 'inline-flex', gap: 0.1, flex: '0 0 auto', opacity: dimmed ? 0.45 : 1 }}>
      {Array.from({ length: count }).map((_, i) => (
        <WhatshotIcon key={i} sx={{ fontSize: '0.8rem', color: FLAME_COLORS[i] }} />
      ))}
    </Box>
  );
};

// A single selectable scale row: name + optional tagline on the left, tier badge on the right.
// A locked row stays tappable: the tap opens the unlock sheet instead of selecting.
const ScaleRow = ({ mode, selected, locked, onSelect }) => {
  const tier = getTierForMode(mode) || 'essentials';
  const tagline = MODE_TAGLINES[mode];
  return (
    <Button
      fullWidth
      variant="outlined"
      onClick={() => onSelect(mode)}
      sx={{
        justifyContent: 'space-between',
        textTransform: 'none',
        textAlign: 'left',
        gap: 1.2,
        px: 1.5,
        py: 1.1,
        mb: 0.9,
        color: selected ? SELECTED_FG : locked ? 'rgba(255, 255, 255, 0.45)' : 'white',
        backgroundColor: selected ? SELECTED_BG : 'transparent',
        borderColor: selected ? SELECTED_BG : locked ? 'rgba(255, 255, 255, 0.06)' : 'rgba(255, 255, 255, 0.1)',
        '&:hover': {
          backgroundColor: selected ? SELECTED_BG_HOVER : 'rgba(30, 30, 47, 0.7)',
          borderColor: selected ? SELECTED_BG_HOVER : 'rgba(255, 255, 255, 0.3)'
        }
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 0 }}>
        <Typography component="span" sx={{ fontSize: '0.92rem', fontWeight: 500, lineHeight: 1.2 }}>
          {mode}
        </Typography>
        {tagline && (
          <Typography
            component="span"
            sx={{
              fontSize: '0.74rem',
              fontWeight: 300,
              color: selected ? 'rgba(0, 0, 0, 0.65)' : locked ? 'rgba(255, 255, 255, 0.35)' : 'rgba(255, 255, 255, 0.7)',
              mt: 0.3
            }}
          >
            {tagline}
          </Typography>
        )}
      </Box>
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.7, flex: '0 0 auto' }}>
        {locked && <LockIcon sx={{ fontSize: '0.85rem', color: 'rgba(255, 255, 255, 0.4)' }} />}
        <FlameRating tier={tier} dimmed={locked} />
      </Box>
    </Button>
  );
};

const TieredScaleSelector = ({
  currentScaleType,
  currentMode,
  onScaleTypeChange,
  onModeChange,
  onLockedMode
}) => {
  const { isModeUnlocked } = useEntitlement();
  // Le pull du compte n'a pas encore rendu son verdict. Sur un appareil qui
  // vient d'activer une invitation, il attend la synchro du contenu et met une
  // dizaine de secondes : pendant tout ce temps la liste montre des cadenas qui
  // vont disparaitre, sans rien dire. Vecu en test le 01/09, et indiscernable
  // d'une panne. On ne le dit que s'il reste quelque chose de verrouille, sinon
  // c'est du bruit chez quelqu'un qui a deja tout.
  const accessSyncing = useAccessSyncing();
  const someLocked = SCALE_TYPES.some((type) =>
    (MODE_NAMES_BY_SCALE[type] || []).some((mode) => !isModeUnlocked(mode))
  );
  // Entry mode: 'feel' or 'name'. Default to 'feel' on first run (beginner-friendly),
  // then respect whatever the user last picked. Read from the user mirror, synced.
  const [entry, setEntry] = useUserSetting(SCALE_ENTRY_KEY, 'feel');

  // Which mood tile is expanded in "By feel". Seed from the current mode if it
  // belongs to a preset, otherwise the first mood.
  const [activeMoodKey, setActiveMoodKey] = useState(() => {
    const match = MOOD_PRESETS.find((m) => m.modes.includes(currentMode));
    return (match || MOOD_PRESETS[0]).key;
  });

  const families = useMemo(
    () => SCALE_TYPES.filter((type) => (MODE_NAMES_BY_SCALE[type] || []).length > 0),
    []
  );

  const activeMood = MOOD_PRESETS.find((m) => m.key === activeMoodKey) || MOOD_PRESETS[0];

  // Selecting any scale: set the scale type (with its tier) first, then the mode.
  // A locked scale routes to the unlock sheet instead of selecting.
  const selectMode = (mode) => {
    if (!isModeUnlocked(mode)) {
      onLockedMode && onLockedMode(mode);
      return;
    }
    onScaleTypeChange(getScaleTypeForMode(mode), getTierForMode(mode));
    onModeChange(mode);
  };

  const segButtonSx = (active) => ({
    flex: 1,
    gap: 0.8,
    py: 1.1,
    borderRadius: '7px',
    textTransform: 'none',
    fontSize: '0.88rem',
    fontWeight: 500,
    color: active ? SELECTED_FG : 'rgba(255, 255, 255, 0.7)',
    backgroundColor: active ? SELECTED_BG : 'transparent',
    '&:hover': {
      backgroundColor: active ? SELECTED_BG_HOVER : 'rgba(255, 255, 255, 0.06)'
    }
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {accessSyncing && someLocked && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 1.25,
            px: 1.25,
            py: 1,
            borderRadius: '10px',
            border: '1px solid rgba(249, 115, 22, 0.35)',
            backgroundColor: 'rgba(249, 115, 22, 0.08)',
          }}
        >
          <CircularProgress size={16} sx={{ color: '#f97316' }} />
          <Typography sx={{ fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.85)' }}>
            Checking your access...
          </Typography>
        </Box>
      )}

      {/* Segmented entry switch */}
      <Box
        sx={{
          display: 'flex',
          gap: 0.5,
          p: 0.4,
          borderRadius: '10px',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          backgroundColor: 'rgba(255, 255, 255, 0.05)'
        }}
      >
        <Button disableElevation onClick={() => setEntry('feel')} sx={segButtonSx(entry === 'feel')}>
          <FavoriteBorderIcon sx={{ fontSize: '1.1rem' }} />
          By feel
        </Button>
        <Button disableElevation onClick={() => setEntry('name')} sx={segButtonSx(entry === 'name')}>
          <FormatListBulletedIcon sx={{ fontSize: '1.1rem' }} />
          By name
        </Button>
      </Box>

      <Box sx={{ mb: 2 }} />

      {entry === 'name' ? (
        <Box>
          {families.map((family) => {
            const modes = MODE_NAMES_BY_SCALE[family] || [];
            const caption = FAMILY_CAPTIONS[family];
            return (
              <Box key={family} sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 1, mb: 1 }}>
                  <Typography
                    component="span"
                    sx={{
                      fontSize: '0.74rem',
                      fontWeight: 700,
                      letterSpacing: '0.05em',
                      textTransform: 'uppercase',
                      color: 'white'
                    }}
                  >
                    {family}
                  </Typography>
                  {caption && (
                    <Typography
                      component="span"
                      sx={{ fontSize: '0.72rem', fontWeight: 300, color: 'rgba(255, 255, 255, 0.45)' }}
                    >
                      {caption}
                    </Typography>
                  )}
                </Box>
                {modes.map((mode) => (
                  <ScaleRow key={mode} mode={mode} selected={currentMode === mode} locked={!isModeUnlocked(mode)} onSelect={selectMode} />
                ))}
              </Box>
            );
          })}
        </Box>
      ) : (
        <Box>
          <Typography sx={{ fontSize: '1.1rem', fontWeight: 300, letterSpacing: '0.02em', mb: 1.5 }}>
            What should it sound like?
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
            {MOOD_PRESETS.map((mood) => {
              const MoodIcon = mood.icon;
              const active = mood.key === activeMoodKey;
              return (
                <Button
                  key={mood.key}
                  variant="outlined"
                  onClick={() => setActiveMoodKey(mood.key)}
                  sx={{
                    justifyContent: 'flex-start',
                    gap: 1.1,
                    px: 1.5,
                    py: 1.3,
                    textTransform: 'none',
                    textAlign: 'left',
                    color: active ? SELECTED_FG : 'white',
                    backgroundColor: active ? SELECTED_BG : 'transparent',
                    borderColor: active ? SELECTED_BG : 'rgba(255, 255, 255, 0.1)',
                    '&:hover': {
                      backgroundColor: active ? SELECTED_BG_HOVER : 'rgba(30, 30, 47, 0.7)',
                      borderColor: active ? SELECTED_BG_HOVER : 'rgba(255, 255, 255, 0.3)'
                    }
                  }}
                >
                  <MoodIcon sx={{ fontSize: '1.25rem', color: active ? SELECTED_FG : 'rgba(255, 255, 255, 0.7)' }} />
                  <Typography component="span" sx={{ fontSize: '0.85rem', fontWeight: 400, lineHeight: 1.15 }}>
                    {mood.label}
                  </Typography>
                </Button>
              );
            })}
          </Box>

          <Box sx={{ mt: 2.5 }}>
            <Typography
              sx={{
                fontSize: '0.74rem',
                fontWeight: 700,
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                color: 'rgba(255, 255, 255, 0.45)',
                mb: 1.2
              }}
            >
              {activeMood.label} · pick one
            </Typography>
            {activeMood.modes.map((mode) => (
              <ScaleRow key={mode} mode={mode} selected={currentMode === mode} locked={!isModeUnlocked(mode)} onSelect={selectMode} />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default TieredScaleSelector;
